import loadRecaptcha from "./load_recaptcha"
import { addEventListenerWithKey } from "shared/lib/utils.js"

// Deliberately generous. Failing open too early is the worse error: a slow-but-working load would
// submit tokenless and the server would reject a legitimate signup. Warming on first interaction
// means this is rarely reached — a blocked host has usually already settled by the time of the
// click, so those users fail open immediately rather than waiting this out.
const TOKEN_TIMEOUT_MS = 6000

const warmWhenIdle = warm => {
  if (typeof requestIdleCallback === "function") {
    requestIdleCallback(warm)
  } else {
    setTimeout(warm, 0)
  }
}

export default function handleRecaptcha(sitekey) {
  const button = document.querySelector(".verify-with-recaptcha")
  if (!button) return

  const form = button.closest("form")
  const email = form.querySelector("input[type='email']")
  const password = form.querySelector("input[type='password']")

  // Load the script ahead of the click — never during it. Fetching at submit would add its latency
  // to every signup and give v3 no interaction window to score. Memoized, so these race harmlessly.
  // Keyed listeners rather than plain ones: these handlers close over per-call state, so a bfcache
  // restore that re-runs this setup would otherwise stack a second copy of each.
  const warm = () => loadRecaptcha(sitekey).catch(() => {})
  addEventListenerWithKey(form, "focusin", "recaptcha_warm_on_focus", warm)
  addEventListenerWithKey(window, "load", "recaptcha_warm_on_idle", () => warmWhenIdle(warm))

  const tokenForSubmit = () => {
    const timeout = new Promise(resolve => setTimeout(() => resolve(null), TOKEN_TIMEOUT_MS))
    const token = loadRecaptcha(sitekey).then(recaptcha => recaptcha.execute(sitekey, { action: "submit" }))

    return Promise.race([token, timeout]).catch(() => null)
  }

  const clickHandler = async e => {
    e.preventDefault()

    if (email && !email.checkValidity()) {
      email.reportValidity()
      return
    }

    if (password && !password.checkValidity()) {
      password.reportValidity()
      return
    }

    // form.submit() skips the submit event, so rails-ujs never applies data-disable-with itself.
    button.disabled = true
    if (button.dataset.disableWith) button.value = button.dataset.disableWith

    const token = await tokenForSubmit()

    // Submit either way. When reCAPTCHA is unreachable the server rejects a tokenless signup with a
    // real error, which beats swallowing the click and leaving the button silently dead.
    if (token) {
      const tokenInput = document.createElement("input")
      tokenInput.setAttribute("type", "hidden")
      tokenInput.setAttribute("name", "recaptcha_token")
      tokenInput.setAttribute("value", token)
      form.appendChild(tokenInput)
    }

    form.submit()
  }

  // Most important of the three: a duplicate click handler would mint two tokens, append two hidden
  // inputs and submit the form twice.
  addEventListenerWithKey(button, "click", "recaptcha_submit", clickHandler)
}
